import { isEmpty } from "lodash";
import type { ReactNode } from "react";

import type {
  CountryCode,
  IPreliminaryLoanAmountData,
} from "@krea/shared/types/common";

import { AdvisoryResult } from "./components/AdvisoryResult";
import { EstimateResult } from "./components/EstimateResult";

/** Matches the h3 above the result card in `LoanWidget` — use on customer pages for pixel parity. */
export const loanWidgetResultTitleClassName = "mb-6 text-page-title";

// ── Props ─────────────────────────────────────────────────────────────────────

export interface ILoanWidgetResultProps {
  countryCode: CountryCode;
  currency: string;
  locale: string;
  organisationName: string;
  email?: string;
  mobilePhoneNumber?: string;
  preliminaryBid: IPreliminaryLoanAmountData;
  requestedLoanAmount: number;
  onGoBack?: () => void;
  onContinueToApply?: () => void;
  utmSourceIdentifier?: string;
  /** Only "production" books the real advisor calendars; omit it and you get the dev one. */
  environment?: string;
  renderReviewBadges?: (countryCode: CountryCode) => ReactNode;
  /** Whether to render the CTA block below the result card. Defaults to true. */
  showCta?: boolean;
  /** Whether to show the USP checklist in the CTA block. Defaults to true. */
  showUsps?: boolean;
  /** Portal container for tooltips/drawers. Pass the widget root to keep portaled content inside scoped styles. */
  portalContainer?: HTMLElement | null;
  /** When provided, renders a "contact us" link at the bottom of the improve-chances accordion. Omit for third-party widget embeds. */
  onContactUs?: () => void;
  /** How the advisory (edge case) CTA opens the booking calendar. Defaults to `"newTab"`, which is right for third-party widget embeds; pass `"modal"` on our own websites and apps. */
  advisoryBooking?: "modal" | "newTab";
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Result view of the loan widget. Splits on the bid type into two self-contained
 * tiers — `AdvisoryResult` (edge case: no automatic estimate) and `EstimateResult`
 * (an estimate with a likelihood). Each owns its full layout.
 */
export const LoanWidgetResult = (props: ILoanWidgetResultProps) => {
  if (isEmpty(props.preliminaryBid)) {
    return null;
  }

  return props.preliminaryBid.useEdgeCaseLogic ? (
    <AdvisoryResult {...props} />
  ) : (
    <EstimateResult {...props} />
  );
};
